import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PRACTICA_SEGUNDOS, PUNTAJE_APRUEBA, type TipoPregunta } from "@huayruro/shared";
import { mutar, useApi } from "../lib/useApi";
import { dbLocal } from "../lib/db-local";
import { navegar } from "../lib/ruta";
import { useCatalogoVenta } from "../lib/useCatalogoVenta";
import { solesCent } from "../lib/money";
import { EVENTO_MI_CUENTA } from "../components/Layout";
import { Button, Chip, Input, cn } from "../components/ui";

// Examen del día (brief 26-oct-2026, paso 3). Patrón del research (docs/research/mi-aprendizaje): modo
// foco —una pregunta a la vez, barra de 10 segmentos, reloj que cambia de color en el último minuto— y las
// respuestas recién al final (es examen, no práctica: nada de «¡Correcto!» a cada paso). El reloj manda el
// SERVIDOR: si se acaba, el examen se cierra con lo respondido.

type Pregunta = {
  orden: number; tipo: TipoPregunta; enunciado: string; puntos: number; opciones: string[] | null; respuesta: string | null;
  abierta_at: string | null; correcta?: string; acierto?: boolean; explicacion?: string | null;
};
type Examen = { id: string; estado: "en_curso" | "terminado"; vence_at: string; puntaje: number | null; puntaje_max: number; aprobado: boolean | null; preguntas: Pregunta[] };
type Estado = { estado: "sin_carga" } | { estado: "pendiente"; productos: number } | { estado: "en_curso" | "terminado"; examen: Examen };

const TIPO: Record<TipoPregunta, string> = {
  generico: "Marca y genérico",
  receta: "Sí o no · vale doble",
  para_que: "¿Para qué sirve?",
  caso: "Caso de mostrador",
  practica: `Práctica · ${PRACTICA_SEGUNDOS} segundos`,
};
const LETRAS = ["A", "B", "C", "D"];
const avisarCabecera = () => window.dispatchEvent(new Event(EVENTO_MI_CUENTA));
const mmss = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export function ExamenDelDia() {
  const { data, error, recargar } = useApi<Estado>("/aprendizaje/examen/hoy");
  const [empezando, setEmpezando] = useState(false);
  const [errorEmpezar, setErrorEmpezar] = useState<string | null>(null);

  async function empezar() {
    setEmpezando(true);
    try {
      await mutar("/aprendizaje/examen/hoy/empezar", { method: "POST" });
      avisarCabecera();
      recargar();
    } catch (e) {
      setErrorEmpezar(e instanceof Error ? e.message : String(e));
    } finally {
      setEmpezando(false);
    }
  }

  if (error) return <p className="p-6 text-center text-[13px] text-accent-ink">{error}</p>;
  if (!data) return <p className="p-6 text-center text-[13px] text-ink-3">Cargando el examen…</p>;
  if (data.estado === "sin_carga") {
    return (
      <Centro>
        <h1 className="text-[20px] font-bold text-ink">Hoy no hay examen todavía</h1>
        <p className="mt-2 text-[14px] text-ink-2">Sale con los productos que tú cargas. Sube tu primera repisa en Inventario › Ingreso.</p>
        <Button onClick={() => navegar("mi-cuenta")} className="mt-4 min-h-[48px]">
          Volver a Mi cuenta
        </Button>
      </Centro>
    );
  }
  if (data.estado === "pendiente") {
    return (
      <Centro>
        <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-3">Examen del día</p>
        <h1 className="mt-1 text-[24px] font-bold text-ink">10 preguntas · 10 minutos</h1>
        <ul className="mt-3 flex flex-col gap-1.5 text-[14px] leading-snug text-ink">
          <li>• Sobre los {data.productos} productos que cargaste hoy.</li>
          <li>• Dos preguntas de «¿pide receta?» valen doble. En total son 12 puntos y apruebas con {PUNTAJE_APRUEBA}.</li>
          <li>• Dos prácticas: buscas el producto y escribes su precio y su stock en {PRACTICA_SEGUNDOS} segundos.</li>
          <li>• Puedes volver atrás y cambiar una respuesta (menos las prácticas). Las respuestas correctas se ven al final.</li>
          <li>• El reloj no se detiene: a los 10 minutos el examen se entrega solo.</li>
        </ul>
        {errorEmpezar && <p className="mt-3 text-[13px] text-accent-ink">{errorEmpezar}</p>}
        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={() => void empezar()} disabled={empezando} className="min-h-[52px] px-6 text-[16px]">
            {empezando ? "Armando el examen…" : "Empezar ahora"}
          </Button>
          <Button variant="outline" onClick={() => navegar("mi-cuenta")} className="min-h-[52px]">
            Más tarde
          </Button>
        </div>
      </Centro>
    );
  }
  return data.estado === "en_curso" ? (
    <EnCurso key={data.examen.id} examen={data.examen} onTerminado={recargar} />
  ) : (
    <Resultado examen={data.examen} />
  );
}

function Centro({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto w-full max-w-[720px] rounded-[16px] border border-line bg-card p-5 sm:p-7">{children}</div>;
}

// ---------------- En curso ----------------
function EnCurso({ examen, onTerminado }: { examen: Examen; onTerminado: () => void }) {
  const preguntas = examen.preguntas;
  const primeraSinResponder = preguntas.findIndex((p) => p.respuesta === null);
  const [i, setI] = useState(primeraSinResponder < 0 ? preguntas.length - 1 : primeraSinResponder);
  const [respuestas, setRespuestas] = useState<Record<number, string | null>>(() => Object.fromEntries(preguntas.map((p) => [p.orden, p.respuesta])));
  const [enviadas, setEnviadas] = useState<Record<number, string | null>>(() => Object.fromEntries(preguntas.map((p) => [p.orden, p.respuesta])));
  const [ahora, setAhora] = useState(Date.now());
  const [error, setError] = useState<string | null>(null);
  const [terminando, setTerminando] = useState(false);
  const p = preguntas[i]!;
  const quedan = Date.parse(examen.vence_at) - ahora;

  useEffect(() => {
    const t = window.setInterval(() => setAhora(Date.now()), 500);
    return () => window.clearInterval(t);
  }, []);

  const terminar = useCallback(async () => {
    setTerminando(true);
    try {
      await mutar(`/aprendizaje/examen/${examen.id}/terminar`, { method: "POST" });
    } catch {
      /* si ya estaba cerrado, la recarga lo muestra */
    }
    avisarCabecera();
    onTerminado();
  }, [examen.id, onTerminado]);

  // Se acabó el tiempo: se entrega solo.
  const entregado = useRef(false);
  useEffect(() => {
    if (quedan <= 0 && !entregado.current) {
      entregado.current = true;
      void terminar();
    }
  }, [quedan, terminar]);

  const enviar = useCallback(
    async (orden: number) => {
      const r = respuestas[orden];
      if (r == null || r === enviadas[orden]) return true;
      try {
        await mutar(`/aprendizaje/examen/${examen.id}/preguntas/${orden}/responder`, { method: "POST", body: { respuesta: r } });
        setEnviadas((e) => ({ ...e, [orden]: r }));
        setError(null);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return false;
      }
    },
    [respuestas, enviadas, examen.id],
  );

  const ir = useCallback(
    async (destino: number) => {
      if (p.tipo !== "practica" && !(await enviar(p.orden))) return;
      setI(Math.max(0, Math.min(preguntas.length - 1, destino)));
    },
    [p, enviar, preguntas.length],
  );

  // Teclado: A–D o 1–4 eligen; S/N en sí o no; Enter = siguiente.
  useEffect(() => {
    if (p.tipo === "practica" || !p.opciones) return;
    const ops = p.opciones;
    const alTeclear = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement | null)?.tagName === "INPUT") return;
      const k = e.key.toUpperCase();
      let idx = LETRAS.indexOf(k);
      if (idx < 0 && /^[1-4]$/.test(k)) idx = Number(k) - 1;
      if (p.tipo === "receta" && (k === "S" || k === "N")) idx = k === "S" ? 0 : 1;
      if (idx >= 0 && idx < ops.length) setRespuestas((r) => ({ ...r, [p.orden]: ops[idx]! }));
      if (e.key === "Enter" && respuestas[p.orden] != null) void (i === preguntas.length - 1 ? confirmarTerminar() : ir(i + 1));
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p, respuestas, i]);

  async function confirmarTerminar() {
    if (p.tipo !== "practica" && !(await enviar(p.orden))) return;
    const sin = preguntas.filter((x) => (respuestas[x.orden] ?? null) === null).length;
    if (sin > 0 && !window.confirm(`Te ${sin === 1 ? "falta 1 pregunta" : `faltan ${sin} preguntas`}. ¿Entregar igual?`)) return;
    await terminar();
  }

  const ultimoMinuto = quedan < 60_000;
  return (
    <div className="mx-auto flex w-full max-w-[760px] flex-col gap-4 pb-24">
      {/* Barra del examen */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            if (window.confirm(`El reloj sigue corriendo. Tienes hasta las ${new Date(examen.vence_at).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit", hour12: false })}. ¿Salir por ahora?`)) navegar("mi-cuenta");
          }}
          aria-label="Salir del examen"
          className="h-11 w-11 flex-none rounded-[10px] border border-line-input bg-card text-[16px] text-ink-emph hover:bg-hover-btn"
        >
          ✕
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-ink">
            Pregunta {i + 1} de {preguntas.length}
          </p>
          <div className="mt-1 flex gap-1" aria-hidden="true">
            {preguntas.map((x, k) => (
              <span key={x.orden} className={cn("h-1.5 flex-1 rounded-full", k === i ? "bg-ink-surface-2" : (respuestas[x.orden] ?? null) !== null ? "bg-ok" : "bg-line-input")} />
            ))}
          </div>
        </div>
        <span role="timer" aria-label={`Quedan ${mmss(quedan)}`} className={cn("min-w-[64px] rounded-[10px] px-2 py-1.5 text-center text-[18px] font-bold tabular-nums", ultimoMinuto ? "bg-accent text-white" : "bg-card text-ink")}>
          {mmss(quedan)}
        </span>
      </div>

      <section className="rounded-[16px] border border-line bg-card p-5 sm:p-7" aria-live="polite">
        <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-3">{TIPO[p.tipo]}</p>
        <h1 className="mt-2 text-[20px] font-bold leading-snug text-ink sm:text-[22px]">{p.enunciado}</h1>
        {p.tipo === "practica" ? (
          <Practica key={p.orden} examenId={examen.id} pregunta={p} yaRespondida={enviadas[p.orden] != null} onRespondida={(r) => {
            setRespuestas((x) => ({ ...x, [p.orden]: r }));
            setEnviadas((x) => ({ ...x, [p.orden]: r }));
          }} />
        ) : (
          <div role="radiogroup" aria-label="Opciones" className="mt-4 flex flex-col gap-2">
            {p.opciones!.map((o, k) => {
              const elegida = respuestas[p.orden] === o;
              return (
                <button
                  key={o}
                  type="button"
                  role="radio"
                  aria-checked={elegida}
                  onClick={() => setRespuestas((r) => ({ ...r, [p.orden]: o }))}
                  className={cn("flex min-h-[56px] items-center gap-3 rounded-[12px] border-2 px-4 py-3 text-left text-[15px] transition-colors", elegida ? "border-ink-surface-2 bg-sel text-ink" : "border-line-input bg-card text-ink hover:bg-hover-btn")}
                >
                  <span className={cn("flex h-8 w-8 flex-none items-center justify-center rounded-full text-[13px] font-bold", elegida ? "bg-ink-surface-2 text-on-dark" : "bg-inset text-ink-2")}>
                    {p.tipo === "receta" ? (k === 0 ? "S" : "N") : LETRAS[k]}
                  </span>
                  <span>{o}</span>
                </button>
              );
            })}
          </div>
        )}
        {error && <p className="mt-3 text-[13px] text-accent-ink">{error}</p>}
      </section>

      <div className="fixed inset-x-0 bottom-0 z-20 flex gap-2 border-t border-line-chrome bg-topbar/95 px-3 py-2 backdrop-blur md:static md:border-0 md:bg-transparent md:p-0">
        <Button variant="outline" onClick={() => void ir(i - 1)} disabled={i === 0} className="min-h-[52px]">
          ← Anterior
        </Button>
        {i < preguntas.length - 1 ? (
          <Button onClick={() => void ir(i + 1)} className="min-h-[52px] flex-1 justify-center text-[15px] md:flex-none md:px-8">
            Siguiente →
          </Button>
        ) : (
          <Button onClick={() => void confirmarTerminar()} disabled={terminando} className="min-h-[52px] flex-1 justify-center bg-ok text-[15px] hover:bg-ok hover:opacity-90 md:flex-none md:px-8">
            {terminando ? "Entregando…" : "Entregar examen"}
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------- Práctica: buscar en el catálogo de la caja, precio y stock en 30 s ----------------
function Practica({ examenId, pregunta, yaRespondida, onRespondida }: { examenId: string; pregunta: Pregunta; yaRespondida: boolean; onRespondida: (r: string) => void }) {
  const catalogo = useCatalogoVenta(dbLocal);
  const [abiertaAt, setAbiertaAt] = useState<number | null>(pregunta.abierta_at ? Date.parse(pregunta.abierta_at) : null);
  const [ahora, setAhora] = useState(Date.now());
  const [q, setQ] = useState("");
  const [precio, setPrecio] = useState("");
  const [stock, setStock] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Abrirla arranca SU reloj en el servidor (lo que cuenta es la hora del servidor, no la de este equipo).
  useEffect(() => {
    if (yaRespondida || abiertaAt) return;
    void mutar<{ abierta_at: string }>(`/aprendizaje/examen/${examenId}/preguntas/${pregunta.orden}/abrir`, { method: "POST" })
      .then(() => setAbiertaAt(Date.now()))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [examenId, pregunta.orden, yaRespondida, abiertaAt]);
  useEffect(() => {
    const t = window.setInterval(() => setAhora(Date.now()), 250);
    return () => window.clearInterval(t);
  }, []);
  const quedan = abiertaAt ? abiertaAt + PRACTICA_SEGUNDOS * 1000 - ahora : PRACTICA_SEGUNDOS * 1000;
  const resultados = useMemo(() => (q.trim().length >= 2 ? catalogo.buscar(q, 8) : []), [catalogo, q]);

  if (yaRespondida) return <p className="mt-4 rounded-[10px] bg-ok-soft px-3 py-2 text-[14px] font-semibold text-ok">Respondida. Sigue con la siguiente.</p>;

  async function responder() {
    setEnviando(true);
    try {
      const r = { precio: precio.trim(), stock: stock.trim() };
      await mutar(`/aprendizaje/examen/${examenId}/preguntas/${pregunta.orden}/responder`, { method: "POST", body: { respuesta: r } });
      onRespondida(JSON.stringify(r));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="mt-4 flex flex-col gap-3">
      <p className={cn("self-start rounded-[9px] px-3 py-1.5 text-[15px] font-bold tabular-nums", quedan <= 10_000 ? "bg-accent text-white" : "bg-inset text-ink")} role="timer">
        {quedan > 0 ? `Quedan ${Math.ceil(quedan / 1000)} s` : "Se pasó el tiempo: responde igual, pero ya no suma"}
      </p>
      <Input autoFocus aria-label="Buscar en el catálogo" placeholder="Búscalo como en el Mostrador" value={q} onChange={(e) => setQ(e.target.value)} className="min-h-[48px] text-[16px]" />
      {resultados.length > 0 && (
        <ul className="flex max-h-[260px] flex-col overflow-y-auto rounded-[10px] border border-line-input">
          {resultados.map((r) => (
            <li key={r.producto_id} className="border-b border-line-row px-3 py-2 last:border-0">
              <p className="text-[14px] font-semibold text-ink">{r.nombre}</p>
              <p className="text-[12.5px] text-ink-2">
                {r.presentaciones.map((x) => `${x.presentacion_nombre}: ${solesCent(Math.round(x.precio_total_dm / 100))}`).join(" · ")} · stock {r.stock_cache ?? "—"}
              </p>
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[12.5px] font-semibold text-ink-2">Precio de la unidad (S/)</span>
          <Input inputMode="decimal" value={precio} onChange={(e) => setPrecio(e.target.value)} placeholder="0.00" className="min-h-[48px] text-[16px]" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12.5px] font-semibold text-ink-2">Unidades en stock</span>
          <Input inputMode="numeric" value={stock} onChange={(e) => setStock(e.target.value)} placeholder="0" className="min-h-[48px] text-[16px]" />
        </label>
      </div>
      {error && <p className="text-[13px] text-accent-ink">{error}</p>}
      <Button onClick={() => void responder()} disabled={enviando || !precio.trim() || !stock.trim()} className="min-h-[48px] justify-center self-start px-6">
        Responder
      </Button>
    </div>
  );
}

// ---------------- Resultado ----------------
function Resultado({ examen }: { examen: Examen }) {
  const falladas = examen.preguntas.filter((p) => !p.acierto);
  const frase = examen.aprobado
    ? falladas.length === 0
      ? "Todo bien. Mañana, otro examen con lo que cargues."
      : "Aprobaste. Repasa lo que fallaste: está abajo con su porqué."
    : "Esta vez no alcanzó. Lee cada respuesta de abajo y repasa sus fichas.";
  return (
    <div className="mx-auto flex w-full max-w-[760px] flex-col gap-4 pb-8">
      <section className="rounded-[16px] border border-line bg-card p-5 text-center sm:p-7">
        <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-3">Examen del día</p>
        <p className="mt-2 text-[56px] font-bold leading-none tabular-nums text-ink">
          {examen.puntaje}/{examen.puntaje_max}
        </p>
        <Chip variant={examen.aprobado ? "ok" : "warn"} className="mt-3 text-[13px]">
          {examen.aprobado ? `Aprobado · mínimo ${PUNTAJE_APRUEBA}` : `No aprobado · mínimo ${PUNTAJE_APRUEBA}`}
        </Chip>
        <p className="mt-3 text-[14.5px] text-ink">{frase}</p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Button onClick={() => navegar("mostrador")} className="min-h-[48px] px-6">
            Volver a caja
          </Button>
          <Button variant="outline" onClick={() => navegar("mi-cuenta")} className="min-h-[48px]">
            Mi cuenta
          </Button>
        </div>
      </section>
      {falladas.length > 0 && (
        <section className="rounded-[16px] border border-line bg-card p-5" aria-label="Lo que fallaste">
          <h2 className="text-[17px] font-bold text-ink">Lo que fallaste ({falladas.length})</h2>
          <ol className="mt-3 flex flex-col gap-3">
            {falladas.map((p) => (
              <li key={p.orden} className="rounded-[12px] border border-line-input p-3">
                <p className="text-[11.5px] font-bold uppercase tracking-[0.06em] text-ink-3">
                  {p.orden}. {TIPO[p.tipo]}
                </p>
                <p className="mt-1 text-[14.5px] font-semibold leading-snug text-ink">{p.enunciado}</p>
                <p className="mt-1.5 text-[13.5px] text-accent-ink">
                  Tu respuesta: <span className="line-through">{respuestaLegible(p)}</span>
                </p>
                <p className="text-[13.5px] font-semibold text-ok">Correcta: {p.correcta}</p>
                {p.explicacion && <p className="mt-1 text-[13px] text-ink-2">{p.explicacion}</p>}
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

function respuestaLegible(p: Pregunta): string {
  if (p.respuesta === null) return "sin responder";
  if (p.tipo !== "practica") return p.respuesta;
  try {
    const r = JSON.parse(p.respuesta) as { precio?: string; stock?: string };
    return `S/ ${r.precio ?? "?"} · ${r.stock ?? "?"} unidades`;
  } catch {
    return p.respuesta;
  }
}
